import { DOCUMENT, InjectionToken, inject } from '@angular/core';
import { playAlertSound } from '@shared/utils';
import { AccessPoint } from '../entities/access-edges.interface';
import type { IRefusalPushWorkerKit } from '../entities/web-push.model';

/**
 * A parte da Document Picture-in-Picture API que o mini-monitor destacado usa.
 * Só existe no Chrome e no Edge de computador; o Firefox e o Safari não a têm.
 */
export interface IDocumentPictureInPicture {
  requestWindow(options?: { width?: number; height?: number }): Promise<Window>;
}

/**
 * A API do navegador entra por injeção para os testes a trocarem por um falso.
 * `null` quando o navegador não a tem — e aí o botão Destacar não aparece.
 */
export const DOCUMENT_PICTURE_IN_PICTURE =
  new InjectionToken<IDocumentPictureInPicture | null>(
    'DOCUMENT_PICTURE_IN_PICTURE',
    {
      providedIn: 'root',
      factory: () => {
        const view = inject(DOCUMENT).defaultView as
          | (Window & { documentPictureInPicture?: IDocumentPictureInPicture })
          | null;
        return view?.documentPictureInPicture ?? null;
      },
    }
  );

/**
 * O que as abas dizem umas às outras sobre o mini-monitor destacado. Só a aba
 * que destacou (a dona) tem a janela; as outras escondem o mini-monitor da
 * página e seguem o ponto de acesso dela, para F2/F3 liberarem o mesmo.
 *
 * Um ponto de acesso escolhido numa aba que não é a dona (seletor da barra,
 * menu da pessoa, busca) não muda o mini-monitor dessa aba: vai como
 * `choose-access-point` para a dona, que troca o destacado, e todas seguem.
 * `owner` é sempre a aba dona — a que fala, ou, no pedido, a que deve atender.
 *
 * `in-front` e `left-front` dizem qual aba está à frente da recepção (visível e
 * com o foco do computador), e `who-is-in-front` é a pergunta de uma aba que
 * acabou de abrir: com uma aba do sistema à frente, a notificação da recusa
 * não aparece.
 */
export type TMiniMonitorMessage =
  | { type: 'who-owns' }
  | {
      type: 'owner' | 'detached' | 'access-point';
      owner: string;
      accessPointId: string | null;
    }
  | { type: 'choose-access-point'; owner: string; accessPoint: AccessPoint }
  | { type: 'returned' | 'owner-gone'; owner: string }
  | { type: 'who-is-in-front' }
  | { type: 'in-front' | 'left-front'; tab: string };

/** Um `BroadcastChannel`, no que o mini-monitor usa dele. */
export interface IMiniMonitorChannel {
  postMessage(message: TMiniMonitorMessage): void;
  addEventListener(
    type: 'message',
    listener: (event: MessageEvent<TMiniMonitorMessage>) => void
  ): void;
  removeEventListener(
    type: 'message',
    listener: (event: MessageEvent<TMiniMonitorMessage>) => void
  ): void;
  close(): void;
}

/**
 * Abre o canal entre as abas da estação. É uma fábrica, e não o canal, para
 * cada aba (cada serviço) ter o seu — nos testes, cada "aba" liga-se ao mesmo
 * barramento falso. `null` onde não há `BroadcastChannel`.
 */
export const MINI_MONITOR_CHANNEL = new InjectionToken<
  () => IMiniMonitorChannel | null
>('MINI_MONITOR_CHANNEL', {
  providedIn: 'root',
  factory: () => () =>
    typeof BroadcastChannel === 'undefined'
      ? null
      : (new BroadcastChannel(
          'actuar-mini-monitor'
        ) as unknown as IMiniMonitorChannel),
});

/**
 * O som da recusa que ainda pode ser liberada. Reaproveita o alerta do painel
 * de retirada ao vivo; entra por injeção para os testes o trocarem por um falso.
 */
export const REFUSAL_ALERT_SOUND = new InjectionToken<() => void>(
  'REFUSAL_ALERT_SOUND',
  {
    providedIn: 'root',
    factory: () => () => playAlertSound('mini-monitor'),
  }
);

/**
 * Uma recusa que ainda pode ser liberada, como o `EdgeControlService` a anuncia:
 * uma vez por tentativa, venha o evento quantas vezes vier. É dado, não aviso —
 * o som do mini-monitor destacado e a notificação do sistema decidem cada um se
 * avisam. Mora junto da regra que a produz (`toReleasableRefusal`).
 */
export type { IReleasableRefusal } from '../entities/blocked-attempt.util';

/** Uma notificação do sistema operativo já mostrada, no que o aviso da recusa usa dela. */
export interface ISystemNotification {
  onclick: ((event: Event) => void) | null;
  onclose: ((event: Event) => void) | null;
  close(): void;
}

/**
 * A parte da Notification API que o aviso da recusa usa: o aviso por cima das
 * outras janelas, mesmo com o navegador atrás delas. A permissão é deste
 * navegador, não do usuário.
 */
export interface ISystemNotifications {
  /** Lida na hora: a recepção pode mudá-la nas definições do site sem recarregar. */
  readonly permission: NotificationPermission;
  /** Só pergunta dentro de um gesto do usuário (o clique no interruptor). */
  requestPermission(): Promise<NotificationPermission>;
  /** Mostra a notificação; `null` quando o navegador se recusa a criá-la. */
  show(title: string, options: NotificationOptions): ISystemNotification | null;
  /**
   * Avisa quando a permissão muda — no prompt, ou nas definições do site — e
   * devolve como deixar de ouvir. Sem a Permissions API, nunca avisa.
   */
  onPermissionChange(listener: () => void): () => void;
}

/**
 * A Notification API entra por injeção para os testes a trocarem por uma falsa.
 * `null` quando o navegador não a tem.
 */
export const SYSTEM_NOTIFICATIONS =
  new InjectionToken<ISystemNotifications | null>('SYSTEM_NOTIFICATIONS', {
    providedIn: 'root',
    factory: () => {
      const view = inject(DOCUMENT).defaultView as
        | (Window & { Notification?: typeof Notification })
        | null;
      const api = view?.Notification;
      if (!api) return null;
      return {
        get permission(): NotificationPermission {
          return api.permission;
        },
        requestPermission: () => api.requestPermission(),
        show: (title, options) => {
          try {
            return new api(title, options);
          } catch (error) {
            // O Chrome do Android só as cria por service worker e lança aqui.
            console.warn('Notificação da recusa não mostrada:', error);
            return null;
          }
        },
        onPermissionChange: (listener) => {
          let stopped = false;
          let stop = (): void => undefined;
          view?.navigator.permissions
            ?.query({ name: 'notifications' })
            .then((status) => {
              if (stopped) return;
              status.addEventListener('change', listener);
              stop = () => status.removeEventListener('change', listener);
            })
            .catch(() => undefined);
          return () => {
            stopped = true;
            stop();
          };
        },
      };
    },
  });

/**
 * Os botões da notificação da recusa: liberar a tentativa e abrir o perfil da
 * pessoa. O Chrome mostra no máximo dois (`Notification.maxActions`).
 */
export type TRefusalNotificationAction = 'release' | 'profile';

/**
 * O que a notificação da recusa leva consigo, e basta para qualquer aba agir
 * sobre ela: todas as abas mostram a mesma notificação (a mesma tag), e o
 * service worker entrega o clique a uma só, que pode não ser a que a mostrou.
 */
export interface IRefusalNotificationData {
  /** O aviso, com os botões, ou o resultado de liberar a partir dele, sem botões. */
  kind: 'refusal' | 'result';
  attemptId: string;
  edgeId: string;
  personId: string | null;
  /** Endereço absoluto do perfil, que o service worker abre; `null` sem pessoa. */
  profileUrl: string | null;
  name: string | null;
  accessPoint: string | null;
  /**
   * Só na notificação que veio do Web Push: o token de uso único com que o
   * worker libera sozinho, sem aba nem sessão. Sem ele, "Liberar" vai para uma aba.
   */
  releaseToken?: string;
  /**
   * Só na do Web Push: quando o prazo de liberar acaba, pelo relógio desta
   * máquina contado a partir da chegada — o worker fecha as vencidas por ele.
   */
  closesAt?: number;
}

/**
 * O que o service worker das notificações (`mini-monitor-notifications-sw.js`)
 * diz à aba que escolheu: `release` para liberar sem a trazer para a frente,
 * `open` para abrir a Live Access Activity. "Ver perfil" o worker resolve
 * sozinho, abrindo o perfil numa aba nova.
 */
export interface IRefusalNotificationMessage {
  source: 'mini-monitor-notifications';
  action: 'release' | 'open';
  data: IRefusalNotificationData;
}

/**
 * Valida uma mensagem do service worker: a página só age sobre as dele, e só
 * com a tentativa — e, para liberar, o equipamento que liberar exige. Abrir a
 * Live Access Activity não precisa dele: a notificação mínima do push, montada
 * sem o equipamento, também a abre.
 */
export function isRefusalNotificationMessage(
  message: unknown
): message is IRefusalNotificationMessage {
  const candidate = message as Partial<IRefusalNotificationMessage> | null;
  const data = candidate?.data;
  return (
    candidate?.source === 'mini-monitor-notifications' &&
    (candidate.action === 'open' ||
      (candidate.action === 'release' &&
        typeof data?.edgeId === 'string' &&
        data.edgeId !== '')) &&
    typeof data?.attemptId === 'string' &&
    data.attemptId !== ''
  );
}

/** Opções de uma notificação mostrada pelo service worker, as únicas com botões. */
export interface IWorkerNotificationOptions extends NotificationOptions {
  actions?: ReadonlyArray<{
    action: TRefusalNotificationAction;
    title: string;
  }>;
  data: IRefusalNotificationData;
}

/** Uma notificação do service worker já mostrada, como `getNotifications` a devolve. */
export interface IWorkerNotification {
  readonly data: unknown;
  close(): void;
}

/** O registo do service worker, no que a notificação da recusa usa dele. */
export interface INotificationWorkerRegistration {
  /** A mesma tag substitui a notificação que já estiver no ecrã. */
  showNotification(
    title: string,
    options: IWorkerNotificationOptions
  ): Promise<void>;
  getNotifications(filter: {
    tag: string;
  }): Promise<readonly IWorkerNotification[]>;
  /** O Web Push deste registo; ausente onde o navegador não o tem. */
  readonly pushManager?: IWebPushManager;
}

/** Uma inscrição Web Push deste navegador, no que a recusa usa dela. */
export interface IWebPushSubscription {
  /** O endereço do serviço de push do navegador: a identidade da inscrição. */
  readonly endpoint: string;
  readonly options: { readonly applicationServerKey: ArrayBuffer | null };
  /** As chaves `p256dh` e `auth`, em base64url. */
  toJSON(): { keys?: Record<string, string> };
  /** Desfaz a inscrição no navegador: o serviço de push passa a recusar o endpoint. */
  unsubscribe(): Promise<boolean>;
}

/** O `PushManager` do registo, no que a recusa usa dele. */
export interface IWebPushManager {
  getSubscription(): Promise<IWebPushSubscription | null>;
  subscribe(options: {
    userVisibleOnly: true;
    applicationServerKey: Uint8Array;
  }): Promise<IWebPushSubscription>;
}

/**
 * O service worker das notificações com botões: uma notificação só tem botões
 * quando é ele que a mostra. É também ele que recebe o Web Push.
 */
export interface INotificationWorker {
  /**
   * Regista o worker (o navegador reaproveita o registo que já houver) e
   * resolve quando a versão desta página está ativa — só então ele mostra
   * notificações e recebe push. Rejeita quando o registo falha.
   */
  register(): Promise<INotificationWorkerRegistration>;
  /**
   * O registo que já houver, sem registar nada: para desfazer uma inscrição
   * sem instalar o worker em quem nunca o teve. `null` sem registo.
   */
  existing(): Promise<INotificationWorkerRegistration | null>;
  /** Ouve as mensagens do worker para esta aba; devolve como deixar de ouvir. */
  onMessage(listener: (message: unknown) => void): () => void;
  /** Guarda o que o worker lê para montar a notificação de um push. */
  saveKit(kit: IRefusalPushWorkerKit): Promise<void>;
  /** Apaga o que `saveKit` guardou. */
  clearKit(): Promise<void>;
}

/**
 * A versão do worker, na URL dele. Mudou o `mini-monitor-notifications-sw.js`?
 * Suba o número: o navegador só volta a buscar um worker já registado quando a
 * URL muda, e sem isso a página esperaria por uma versão que nunca chega (um
 * push a cair num worker sem o ouvinte de `push` vira a notificação genérica do
 * Chrome, "Este site foi atualizado em segundo plano").
 */
const NOTIFICATION_WORKER_VERSION = 2;

/** A cache onde o worker lê o kit; o mesmo nome do lado do worker. */
const NOTIFICATION_WORKER_KIT_CACHE = 'mini-monitor-notifications';

/**
 * O service worker entra por injeção para os testes o trocarem por um falso.
 * `null` onde não há service worker (navegador sem suporte, página sem HTTPS):
 * aí a notificação vem sem botões.
 *
 * O worker é servido de `/assets/`, e não da raiz do site: a publicação só
 * copia para a raiz o `index.html`, `assets/`, `.well-known/` e os favicons; o
 * resto vai para `/releases/<versão>/`. O escopo é uma pasta que nenhuma página
 * visita — o worker não controla página nenhuma nem vê os pedidos dela.
 */
export const NOTIFICATION_WORKER =
  new InjectionToken<INotificationWorker | null>('NOTIFICATION_WORKER', {
    providedIn: 'root',
    factory: () => {
      const doc = inject(DOCUMENT);
      const view = doc.defaultView;
      const container = view?.navigator.serviceWorker;
      if (!container) return null;

      const scriptUrl = new URL(
        `assets/mini-monitor-notifications-sw.js?v=${NOTIFICATION_WORKER_VERSION}`,
        doc.baseURI
      ).href;
      const scope = new URL('assets/mini-monitor-notifications/', doc.baseURI)
        .href;
      // O mesmo endereço que o worker lê, a partir do escopo dele.
      const kitUrl = new URL('kit.json', scope).href;

      // `navigator.serviceWorker.ready` nunca resolve aqui: espera um worker
      // que controle a página, e este não controla nenhuma. E um worker ativo
      // não basta: pode ser a versão anterior, ainda sem o ouvinte de `push`.
      const whenActive = (
        registration: ServiceWorkerRegistration
      ): Promise<void> => {
        if (registration.active?.scriptURL === scriptUrl) {
          return Promise.resolve();
        }
        const worker = registration.installing ?? registration.waiting;
        if (!worker) {
          // Outra aba, com outra versão da página, registou depois desta: o
          // ficheiro servido é o mesmo, e é o que está ativo.
          return registration.active
            ? Promise.resolve()
            : Promise.reject(new Error('Service worker sem versão a ativar.'));
        }
        return new Promise((resolve, reject) => {
          const onStateChange = (): void => {
            if (worker.state !== 'activated' && worker.state !== 'redundant') {
              return;
            }
            worker.removeEventListener('statechange', onStateChange);
            if (worker.state === 'activated') resolve();
            else reject(new Error('Service worker descartado na instalação.'));
          };
          worker.addEventListener('statechange', onStateChange);
          onStateChange();
        });
      };

      // Uma vez por carga, e sem esperar: se a publicação serviu o ficheiro
      // antigo na URL nova (a CDN ainda sem invalidar), a carga seguinte já
      // encontra o worker certo.
      let updateRequested = false;
      const requestUpdate = (registration: ServiceWorkerRegistration): void => {
        if (updateRequested) return;
        updateRequested = true;
        registration.update().catch(() => undefined);
      };

      return {
        register: async () => {
          const registration = await container.register(scriptUrl, { scope });
          await whenActive(registration);
          requestUpdate(registration);
          return registration as unknown as INotificationWorkerRegistration;
        },
        existing: async () =>
          ((await container.getRegistration(scope)) ??
            null) as unknown as INotificationWorkerRegistration | null,
        saveKit: async (kit) => {
          if (!view?.caches) return;
          const cache = await view.caches.open(NOTIFICATION_WORKER_KIT_CACHE);
          await cache.put(
            kitUrl,
            new view.Response(JSON.stringify(kit), {
              headers: { 'Content-Type': 'application/json' },
            })
          );
        },
        clearKit: async () => {
          await view?.caches?.delete(NOTIFICATION_WORKER_KIT_CACHE);
        },
        onMessage: (listener) => {
          const onMessage = (event: MessageEvent): void => listener(event.data);
          container.addEventListener('message', onMessage);
          // As mensagens ficam em fila até isto, ou até ao DOMContentLoaded.
          container.startMessages();
          return () => container.removeEventListener('message', onMessage);
        },
      };
    },
  });

/** Lugares da janela destacada, de cima para baixo. */
export type TDetachedSlot = 'miniMonitor' | 'activity';

/** A janela destacada já vestida: os seus lugares e como deixar de a vestir. */
export interface IDetachedWindowLayout {
  /** O elemento de cada lugar, onde o conteúdo registado entra. */
  slots: ReadonlyMap<TDetachedSlot, HTMLElement>;
  /** Para o que ficou a correr para vestir a janela; chamado quando ela fecha. */
  dispose: () => void;
}

/**
 * Veste o documento da janela destacada — o visual da página e os lugares — e
 * devolve os lugares. É apresentação, e por isso não mora aqui: vem da feature,
 * por `EdgeControlService.detach`. O serviço só abre a janela, leva o conteúdo
 * registado para os lugares e devolve-o à página quando ela fecha.
 */
export type TDetachedWindowLayout = (doc: Document) => IDetachedWindowLayout;
